import { useState } from "react";
import type { ApiClient } from "../../api/client";
import type { FailedPrint, PrintStatus } from "../../api/types";
import { t } from "../../i18n";
import { formatTime } from "../../lib/time";
import { Dialog } from "../../ui/Dialog";

interface Props {
  api: ApiClient;
  status: PrintStatus;
  /** The failed jobs this till still shows (not "Negeren"). */
  failed: readonly FailedPrint[];
  onDismiss: (ids: readonly string[]) => void;
  /** After "Opnieuw": the state is asked again. */
  onChanged: () => void;
  toast: (message: string, tone?: "info" | "bad") => void;
  report: (error: unknown) => void;
  onClose: () => void;
}

/**
 * "🖨 Afdrukken" from the top bar (blok I): which computers print (the Tafo app for Windows), what waits
 * and what was not printed in the last hour, with "Opnieuw" (queued again for the same printer) and
 * "Negeren" (only on this till).
 */
export function PrintProblemsDialog({ api, status, failed, onDismiss, onChanged, toast, report, onClose }: Props) {
  const text = t.print.problems;
  const [busy, setBusy] = useState(false);

  const again = async (jobs: readonly FailedPrint[]) => {
    setBusy(true);
    try {
      for (const job of jobs) {
        await api.request("POST", `/device/print-jobs/${job.id}/retry`);
        toast(text.queued(job.title, job.printer));
      }
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
      onChanged();
    }
  };

  return (
    <Dialog
      title={text.title}
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          {failed.length > 1 && (
            <button type="button" className="btn" disabled={busy} onClick={() => void again(failed)}>
              {text.againAll}
            </button>
          )}
          <button type="button" className="btn primary" data-autofocus onClick={onClose}>
            OK
          </button>
        </>
      }
    >
      <div className="zone">{text.agents}</div>
      {status.agents.length === 0 ? (
        <p className="warnbox">{text.noAgent}</p>
      ) : (
        <ul className="agents">
          {status.agents.map((agent) => (
            <li key={agent.deviceId}>
              <span className="pill ok">●</span> {text.agent(agent.name, agent.version)}
            </li>
          ))}
        </ul>
      )}
      {status.waiting > 0 && <p className="note">{text.waiting(status.waiting)}</p>}

      <div className="zone">{t.print.failed(failed.length)}</div>
      {failed.length === 0 ? (
        <p className="note">{text.none}</p>
      ) : (
        <>
          <p className="note">{text.note}</p>
          <div className="tablewrap">
            <table className="stafftbl printfails">
              <thead>
                <tr>
                  <th>{text.columns.time}</th>
                  <th>{text.columns.what}</th>
                  <th>{text.columns.printer}</th>
                  <th>{text.columns.reason}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {failed.map((job) => (
                  <tr key={job.id}>
                    <td className="num">{formatTime(job.failedAt)}</td>
                    <td>{job.title}</td>
                    <td>{job.printer}</td>
                    <td>{job.error === "expired" ? text.expired : job.error}</td>
                    <td>
                      <div className="rowbtns">
                        <button
                          type="button"
                          className="btn small"
                          disabled={busy}
                          aria-label={`${text.again} ${job.title} ${job.printer}`}
                          onClick={() => void again([job])}
                        >
                          {text.again}
                        </button>
                        <button
                          type="button"
                          className="btn small"
                          aria-label={`${text.dismiss} ${job.title} ${job.printer}`}
                          onClick={() => {
                            onDismiss([job.id]);
                          }}
                        >
                          {text.dismiss}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Dialog>
  );
}
